import { Flex, Grid, Typography } from "antd";
import type React from "react";

const { useBreakpoint } = Grid;

/**
 * The animated wordmark: the brand kit's darker-blue GIF, cropped to the logo with its white
 * background keyed out, so it sits on the hero gradient without a box. Reduced motion gets the
 * static SVG.
 */
function AnimatedWordmark({ height }: { height: number }) {
	return (
		<picture style={{ display: "flex", alignSelf: "flex-start" }}>
			<source
				srcSet="/assets/title.svg"
				media="(prefers-reduced-motion: reduce)"
			/>
			<img
				src="/assets/title-animated.gif"
				alt="StratoNext"
				style={{ height }}
			/>
		</picture>
	);
}

/** Two-column auth page shell: gradient hero on the left, form panel on the right.
 *  Pass form content as children — it lands centered in the right panel. */
export function AuthPageLayout({ children }: { children: React.ReactNode }) {
	const screens = useBreakpoint();
	const isMobile = !screens.md;

	return (
		<div style={{ display: "flex", minHeight: "100vh" }}>
			{/* Left hero panel */}
			{!isMobile && (
				<div
					style={{
						flex: "0 0 58%",
						background:
							"radial-gradient(circle at 15% 15%, rgba(24,144,255,.28), transparent 35%)," +
							"radial-gradient(circle at 85% 75%, rgba(24,100,255,.22), transparent 40%)," +
							"linear-gradient(155deg, #F3F7FC 0%, #DCEBFF 40%, #AFD4F8 100%)", // brand Mist → Light Blue
						display: "flex",
						flexDirection: "column",
						padding: "40px 48px",
						overflow: "hidden",
					}}
				>
					<AnimatedWordmark height={42} />
					<div style={{ marginTop: "auto", maxWidth: 480 }}>
						<Typography.Title
							level={2}
							style={{ margin: 0, fontWeight: 700, color: "#0a1633" }}
						>
							Human-led.
						</Typography.Title>
						<Typography.Title
							level={2}
							style={{ margin: "0 0 16px", fontWeight: 700, color: "#1864ff" }}
						>
							Agent-operated.
						</Typography.Title>
						<Typography.Text
							style={{ color: "#5b6782", fontSize: 15, lineHeight: 1.6 }}
						>
							StratoNext is an infrastructure for autonomous DevOps, where
							trusted AI agents monitor, troubleshoot, and execute operational
							tasks.
						</Typography.Text>
					</div>
				</div>
			)}

			{/* Right form panel */}
			<div
				style={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: isMobile ? "48px 24px" : 48,
					background: "#fff",
					position: "relative",
				}}
			>
				{/* Mobile wordmark */}
				{isMobile && (
					<div style={{ marginBottom: 32 }}>
						<AnimatedWordmark height={38} />
					</div>
				)}

				{/* Logo badge (desktop) */}
				{!isMobile && (
					<div
						style={{
							width: 72,
							height: 72,
							borderRadius: 18,
							boxShadow: "0 4px 24px rgba(0,0,0,0.09)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "#fff",
							marginBottom: 24,
						}}
					>
						<img
							src="/assets/logo.svg"
							alt="StratoNext"
							style={{ height: 44 }}
						/>
					</div>
				)}

				<div style={{ width: "100%", maxWidth: 360 }}>{children}</div>

				{/* Footer */}
				<Flex
					align="center"
					gap={isMobile ? 8 : 16}
					wrap
					justify="center"
					style={{ position: "absolute", bottom: 24, padding: "0 16px" }}
				>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						© 2026 StratoNext. All rights reserved.
					</Typography.Text>
					<Typography.Link
						href="https://www.stratonext.ai/privacy"
						style={{ fontSize: 12 }}
					>
						Privacy
					</Typography.Link>
					<Typography.Link
						href="https://www.stratonext.ai/terms"
						style={{ fontSize: 12 }}
					>
						Terms
					</Typography.Link>
				</Flex>
			</div>
		</div>
	);
}
